import { Action, createAction, handleActions } from "redux-actions";
import moment from "moment";
import { queueMessage } from "~shared/snackbarsProvider/actions";
import EmailValidator from "email-validator";
import validateDominio from "~libs/dominioValidator";
import {
  Cliente,
  GetDatosPagoResponse,
  GetLogSolicitudResponse,
  GetSolicitudEmisionResponse,
  InfoEmision,
  Originador,
  PatchRechazarSolicitudRequest,
  PostEmisionManualRequest,
  Vehiculo,
} from "~api/types";
import {
  Base64File,
  CampoFormulario,
  CuotaFormulario,
  CuotasFormulario,
  Dispatch,
  FechaFormulario,
  GetState,
  Services,
  ServicesEmisionesManuales,
} from "../types";

/**
 * El slice arranca con la cáscara del detalle antes de que responda el backend:
 * las cards leen campos anidados sin chequear, así que los sub-objetos tienen
 * que existir aunque estén vacíos.
 */
type DetalleSolicitud = Omit<
  Partial<GetSolicitudEmisionResponse>,
  "datosCliente" | "datosEmision" | "datosOriginador" | "datosVehiculo"
> & {
  datosCliente: Partial<Cliente>;
  datosEmision: Partial<InfoEmision>;
  datosOriginador: Partial<Originador>;
  datosVehiculo: Partial<Vehiculo>;
};

interface DatosPagoState {
  datosPago: string;
  fechaVencimientoTarjetaCredito: string;
}

interface DetalleSolicitudEmisionState {
  detalleSolicitudEmision: DetalleSolicitud;
  isLoading: boolean;
  isLoadingLog: boolean;
  polizaCreada: boolean;
  creatingPoliza: boolean;
  datosPago: DatosPagoState | null;
  emailSelected: CampoFormulario<string | null>;
  dominioSelected: CampoFormulario<string | null>;
  vigenciaDesdeSelected: CampoFormulario<FechaFormulario>;
  vigenciaHastaSelected: CampoFormulario<FechaFormulario>;
  nroMotorSelected: CampoFormulario<string | null>;
  nroChasisSelected: CampoFormulario<string | null>;
  premioTotalSelected: CampoFormulario<string | number | null>;
  sumaAseguradaSelected: CampoFormulario<string | number | null>;
  numeroPolizaSelected: CampoFormulario<string | null>;
  polizaFileNameSelected: CampoFormulario<string | null>;
  isProcessingPoliza: boolean;
  isProcessingTarjeta: boolean;
  isProcessingCedulaMercosur: boolean;
  isProcessingCuponera: boolean;
  tarjetaFileNameSelected: CampoFormulario<string | null>;
  cedulaMercosurFileNameSelected: CampoFormulario<string | null>;
  cuponeraFileNameSelected: CampoFormulario<string | null>;
  vigenciaSelected: CampoFormulario<number>;
  cantidadCuotasSelected: CampoFormulario<number>;
  cuotasSelected: CuotasFormulario;
  submitting: boolean;
  loadingError: boolean;
  solicitudRechazada: boolean;
  base64Files: Record<string, Base64File>;
  downloadFiles: boolean;
  fotoFrontalFileNameSelected: CampoFormulario<string | null>;
  fotoTraseraFileNameSelected: CampoFormulario<string | null>;
  fotoLateralIzquierdoFileNameSelected: CampoFormulario<string | null>;
  fotoLateralDerechoFileNameSelected: CampoFormulario<string | null>;
  logsUsuario?: GetLogSolicitudResponse | null;
  codigoOrganizadorSelected?: string;
  codigoProductorSelected?: string;
  codigoVendedorSelected?: string;
}

const getDetalleSolicitudEmisionInited = createAction(
  "OBTENER_DETALLE_SOLICITUDEMISION_INITED"
);
/**
 * Se tipa la respuesta como no nula a propósito: si el backend devolviera null,
 * el reducer rompe al leer los sub-objetos y el throw queda atrapado por el
 * `catch` del thunk, que despacha el `...Failed` y deja la pantalla en error.
 */
const getDetalleSolicitudEmisionCompleted = createAction(
  "OBTENER_DETALLE_SOLICITUDEMISION_COMPLETED",
  (detalleSolicitudEmision: GetSolicitudEmisionResponse) => ({
    detalleSolicitudEmision,
  })
);
const getDetalleSolicitudEmisionFailed = createAction(
  "OBTENER_DETALLE_SOLICITUDEMISION_FAILED",
  (error: unknown) => ({
    error,
  })
);

const getDetalleSolicitudEmision = (id: string) => {
  return async (dispatch: Dispatch, getState: GetState, services: Services) => {
    try {
      dispatch(getDetalleSolicitudEmisionInited());
      dispatch(clearCuotas());
      const response = await services.api
        .solicitudesEmision()
        .getDetalleSolicitudEmision(id);

      dispatch(getDetalleSolicitudEmisionCompleted(response));
    } catch (error) {
      console.error(error);
      dispatch(getDetalleSolicitudEmisionFailed(error));
    }
  };
};

const postEmisionManualInited = createAction("PROCESAR_EMISION_MANUAL_INITED");
const postEmisionManualCompleted = createAction(
  "PROCESAR_EMISION_MANUAL_COMPLETED",
  (response: Response) => undefined
);
const postEmisionManualFailed = createAction(
  "PROCESAR_EMISION_MANUAL_FAILED",
  (error: unknown) => ({
    error,
  })
);

const postEmisionManual = (
  solicitudEmisionId: string,
  data: PostEmisionManualRequest
) => {
  return async (
    dispatch: Dispatch,
    getState: GetState,
    services: ServicesEmisionesManuales
  ) => {
    try {
      dispatch(postEmisionManualInited());

      const response = await services.api
        .emisionesManuales()
        .postEmisionManual(solicitudEmisionId, data);

      dispatch(postEmisionManualCompleted(response));
    } catch (error) {
      console.error(error);
      dispatch(postEmisionManualFailed(error));
      dispatch(
        queueMessage("Ocurrió un error al crear la póliza. Intente nuevamente.")
      );
    }
  };
};

const patchRechazarSolicitudInited = createAction("RECHAZAR_SOLICITUD_INITED");
const patchRechazarSolicitudCompleted = createAction(
  "RECHAZAR_SOLICITUD_COMPLETED",
  (response: Response) => undefined
);
const patchRechazarSolicitudFailed = createAction(
  "RECHAZAR_SOLICITUD_FAILED",
  (error: unknown) => ({
    error,
  })
);

const patchRechazarSolicitud = (
  solicitudEmisionId: string,
  data: PatchRechazarSolicitudRequest
) => {
  return async (dispatch: Dispatch, getState: GetState, services: Services) => {
    try {
      dispatch(patchRechazarSolicitudInited());

      const response = await services.api
        .solicitudesEmision()
        .patchRechazarSolicitud(solicitudEmisionId, data);

      dispatch(patchRechazarSolicitudCompleted(response));
    } catch (error) {
      console.error(error);
      dispatch(patchRechazarSolicitudFailed(error));
      dispatch(
        queueMessage(
          "Ocurrió un error al rechazar la solicitud. Intente nuevamente."
        )
      );
    }
  };
};

const getDatosPagoInited = createAction("OBTENER_DATOS_PAGO_INITED");
const getDatosPagoCompleted = createAction(
  "OBTENER_DATOS_PAGO_COMPLETED",
  (response: GetDatosPagoResponse | null | undefined) => {
    let datosPago = "";
    let fechaVencimientoTarjetaCredito = "";

    if (response != null) {
      datosPago = response.datoDePago;
      fechaVencimientoTarjetaCredito = response.fechaVencimientoTarjetaCredito;
    }

    return { datosPago, fechaVencimientoTarjetaCredito };
  }
);
const getDatosPagoFailed = createAction(
  "OBTENER_DATOS_PAGO_FAILED",
  (error: unknown) => ({
    error,
  })
);

const setEmail = createAction(
  "SET_EMAIL",
  (emailSelected: CampoFormulario<string | null>) => ({
    emailSelected,
  })
);
const setDominio = createAction(
  "SET_DOMINIO",
  (dominioSelected: CampoFormulario<string | null>) => ({
    dominioSelected,
  })
);
const setVigenciaDesde = createAction(
  "SET_VIGENCIA_DESDE",
  (vigenciaDesdeSelected: CampoFormulario<FechaFormulario>) => ({
    vigenciaDesdeSelected,
  })
);
const setVigenciaHasta = createAction(
  "SET_VIGENCIA_HASTA",
  (vigenciaHastaSelected: CampoFormulario<FechaFormulario>) => ({
    vigenciaHastaSelected,
  })
);

const setVigencia = createAction(
  "SET_VIGENCIA",
  (vigenciaSelected: CampoFormulario<number>) => ({
    vigenciaSelected,
  })
);

const setCantidadCuotas = createAction(
  "SET_CANTIDAD_CUOTAS",
  (cantidadCuotasSelected: CampoFormulario<number>) => ({
    cantidadCuotasSelected,
  })
);
const setNroMotor = createAction(
  "SET_NRO_MOTOR",
  (nroMotorSelected: CampoFormulario<string | null>) => ({
    nroMotorSelected,
  })
);
const setNroChasis = createAction(
  "SET_NRO_CHASIS",
  (nroChasisSelected: CampoFormulario<string | null>) => ({
    nroChasisSelected,
  })
);
const setPremioTotal = createAction(
  "SET_PREMIO_TOTAL",
  (premioTotalSelected: CampoFormulario<string | number | null>) => ({
    premioTotalSelected,
  })
);
const setSumaAsegurada = createAction(
  "SET_SUMA_ASEGURADA",
  (sumaAseguradaSelected: CampoFormulario<string | number | null>) => ({
    sumaAseguradaSelected,
  })
);
const setNumeroPoliza = createAction(
  "SET_NUMERO_POLIZA",
  (numeroPolizaSelected: CampoFormulario<string | null>) => ({
    numeroPolizaSelected,
  })
);
const setIsProcessingPoliza = createAction(
  "SET_PROCESSING_POLIZA",
  (isProcessingPoliza: boolean) => ({
    isProcessingPoliza,
  })
);
const setIsProcessingTarjeta = createAction(
  "SET_PROCESSING_TARJETA",
  (isProcessingTarjeta: boolean) => ({
    isProcessingTarjeta,
  })
);
const setIsProcessingCedulaMercosur = createAction(
  "SET_PROCESSING_CEDULA_MERCOSUR",
  (isProcessingCedulaMercosur: boolean) => ({
    isProcessingCedulaMercosur,
  })
);
const setIsProcessingCuponera = createAction(
  "SET_PROCESSING_CUPONERA",
  (isProcessingCuponera: boolean) => ({
    isProcessingCuponera,
  })
);
const setPolizaFileName = createAction(
  "SET_POLIZA_FILENAME",
  (polizaFileNameSelected: CampoFormulario<string | null>) => ({
    polizaFileNameSelected,
  })
);
const setTarjetaFileName = createAction(
  "SET_TARJETA_FILENAME",
  (tarjetaFileNameSelected: CampoFormulario<string | null>) => ({
    tarjetaFileNameSelected,
  })
);

const setCedulaMercosurFileName = createAction(
  "SET_CEDULA_MERCOSUR_FILENAME",
  (cedulaMercosurFileNameSelected: CampoFormulario<string | null>) => ({
    cedulaMercosurFileNameSelected,
  })
);

const setCuponeraFileName = createAction(
  "SET_CUPONERA_FILENAME",
  (cuponeraFileNameSelected: CampoFormulario<string | null>) => ({
    cuponeraFileNameSelected,
  })
);

const setFotoFrontalFileName = createAction(
  "SET_FOTO_FRONTAL_FILENAME",
  (fotoFrontalFileNameSelected: CampoFormulario<string | null>) => ({
    fotoFrontalFileNameSelected,
  })
);

const setFotoTraseraFileName = createAction(
  "SET_FOTO_TRASERA_FILENAME",
  (fotoTraseraFileNameSelected: CampoFormulario<string | null>) => ({
    fotoTraseraFileNameSelected,
  })
);

const setFotoLateralIzquierdoFileName = createAction(
  "SET_FOTO_LATERAL_IZQUIERDO_FILENAME",
  (fotoLateralIzquierdoFileNameSelected: CampoFormulario<string | null>) => ({
    fotoLateralIzquierdoFileNameSelected,
  })
);

const setFotoLateralDerechoFileName = createAction(
  "SET_FOTO_LATERAL_DERECHO_FILENAME",
  (fotoLateralDerechoFileNameSelected: CampoFormulario<string | null>) => ({
    fotoLateralDerechoFileNameSelected,
  })
);

const setCuota = createAction(
  "SET_CUOTA",
  (cuotaSelected: CuotaFormulario) => ({
    cuotaSelected,
  })
);

const setBase64File = createAction(
  "SET_BASE64_FILE",
  (base64File: Base64File) => ({
    base64File,
  })
);

const getDatosPago = (id: string) => {
  return async (dispatch: Dispatch, getState: GetState, services: Services) => {
    try {
      dispatch(getDatosPagoInited());

      const response = await services.api.solicitudesEmision().getDatosPago(id);

      dispatch(getDatosPagoCompleted(response));
    } catch (error) {
      console.error(error);
      dispatch(getDatosPagoFailed(error));
    }
  };
};

const clearDatosPago = createAction(
  "CLEAR_DATOS_PAGO",
  (datosPago?: DatosPagoState | null) => ({
    datosPago,
  })
);

const getLogsUsuarioInited = createAction(
  "OBTENER_LOGS_USUARIO_SOLICITUD_EMISION_INITED"
);

const getLogsUsuario = (id: string) => {
  return async (dispatch: Dispatch, getState: GetState, services: Services) => {
    try {
      dispatch(getLogsUsuarioInited());

      const response = await services.api
        .solicitudesEmision()
        .getLogsUsuario(id);

      dispatch(getLogsUsuarioCompleted(response));
    } catch (error) {
      console.error(error);
      dispatch(getLogsUsuarioFailed(error));
    }
  };
};

const getLogsUsuarioCompleted = createAction(
  "OBTENER_LOGS_USUARIO_SOLICITUD_EMISION_COMPLETED",
  (response: GetLogSolicitudResponse) => {
    let logsUsuario = response;

    return { logsUsuario };
  }
);

const getLogsUsuarioFailed = createAction(
  "OBTENER_LOGS_USUARIO_SOLICITUD_EMISION_FAILED",
  (error: unknown) => ({
    error,
  })
);

const clearBase64File = createAction("CLEAR_BASE64_FILE", () => ({}));

const clearData = createAction("CLEAR_DATA", () => ({}));
const clearCuotas = createAction("CLEAR_CUOTAS", () => ({}));

const setSubmitting = createAction("SET_SUBMITTING", (submitting: boolean) => ({
  submitting,
}));

const detalleSolicitudEmisionInitialState: DetalleSolicitudEmisionState = {
  detalleSolicitudEmision: {
    datosCliente: {},
    datosEmision: {},
    datosOriginador: {},
    datosVehiculo: {},
    // El objeto vacío es truthy: hasta que responde el detalle, las validaciones
    // de fotos del vehículo no corren.
    fotosVehiculo: {} as unknown as string[],
  },
  isLoading: true,
  isLoadingLog: true,
  polizaCreada: false,
  creatingPoliza: false,
  datosPago: null,
  emailSelected: { value: "", valid: false, focus: false },
  dominioSelected: { value: "", valid: false, focus: false },
  vigenciaDesdeSelected: { value: new Date(), valid: true, focus: false },
  vigenciaHastaSelected: { value: new Date(), valid: false, focus: false },
  nroMotorSelected: { value: "", valid: false, focus: false },
  nroChasisSelected: { value: "", valid: false, focus: false },
  premioTotalSelected: { value: 0, valid: false, focus: false },
  sumaAseguradaSelected: { value: 0, valid: false, focus: false },
  numeroPolizaSelected: { value: "", valid: false, focus: false },
  polizaFileNameSelected: { value: null, valid: false, focus: false },
  isProcessingPoliza: false,
  isProcessingTarjeta: false,
  isProcessingCedulaMercosur: false,
  isProcessingCuponera: false,
  tarjetaFileNameSelected: { value: null, valid: false, focus: false },
  cedulaMercosurFileNameSelected: { value: null, valid: false, focus: false },
  cuponeraFileNameSelected: { value: null, valid: false, focus: false },
  vigenciaSelected: { value: 6, valid: true },
  cantidadCuotasSelected: { value: 6, valid: true },
  cuotasSelected: {},
  submitting: false,
  loadingError: false,
  solicitudRechazada: false,
  base64Files: {},
  downloadFiles: false,
  fotoFrontalFileNameSelected: { value: null, valid: false, focus: false },
  fotoTraseraFileNameSelected: { value: null, valid: false, focus: false },
  fotoLateralIzquierdoFileNameSelected: {
    value: null,
    valid: false,
    focus: false,
  },
  fotoLateralDerechoFileNameSelected: {
    value: null,
    valid: false,
    focus: false,
  },
};

const detalleSolicitudEmisionReducer = handleActions<
  DetalleSolicitudEmisionState,
  any
>(
  {
    [getDetalleSolicitudEmisionInited.toString()]:
      (): DetalleSolicitudEmisionState => {
        return {
          ...detalleSolicitudEmisionInitialState,
        };
      },
    [getDetalleSolicitudEmisionCompleted.toString()]: (
      state: DetalleSolicitudEmisionState,
      action: Action<{ detalleSolicitudEmision: GetSolicitudEmisionResponse }>
    ): DetalleSolicitudEmisionState => {
      const detalle = action.payload.detalleSolicitudEmision;
      return {
        ...state,
        detalleSolicitudEmision: detalle,
        isLoading: false,
        dominioSelected: {
          value: detalle.datosVehiculo.dominio,
          valid: Boolean(
            detalle.datosVehiculo.dominio &&
            validateDominio(detalle.datosVehiculo.dominio)
          ),
          focus: false,
        },
        emailSelected: {
          value: detalle.datosCliente.email,
          valid: Boolean(
            detalle.datosCliente.email &&
            EmailValidator.validate(detalle.datosCliente.email)
          ),
          focus: false,
        },
        nroChasisSelected: {
          value: detalle.datosVehiculo.numeroChasis,
          valid: detalle.datosVehiculo.numeroChasis ? true : false,
          focus: false,
        },
        nroMotorSelected: {
          value: detalle.datosVehiculo.numeroMotor,
          valid: detalle.datosVehiculo.numeroMotor ? true : false,
          focus: false,
        },
        sumaAseguradaSelected: {
          value: detalle.datosPoliza
            ? detalle.datosPoliza.sumaAsegurada
            : detalle.datosVehiculo.sumaAsegurada,
          valid: detalle.datosPoliza
            ? detalle.datosPoliza.sumaAsegurada !== 0
            : detalle.datosVehiculo.sumaAsegurada !== 0,
          focus: false,
        },
        vigenciaSelected: {
          value:
            detalle.datosPoliza && detalle.datosPoliza.vigencia !== 0
              ? detalle.datosPoliza.vigencia
              : detalle.cantidadCuotas,
          valid: true,
          focus: false,
        },
        cantidadCuotasSelected: {
          value:
            detalle.datosPoliza && detalle.datosPoliza.vigencia !== 0
              ? detalle.datosPoliza.cantidadCuotas
              : detalle.cantidadCuotas,
          valid: true,
          focus: false,
        },
        vigenciaDesdeSelected: {
          value:
            detalle.datosPoliza && detalle.datosPoliza.fechaVigenciaDesde
              ? detalle.datosPoliza.fechaVigenciaDesde
              : detalle.vigenciaDesde,
          valid: true,
          focus: false,
        },
        vigenciaHastaSelected: {
          value:
            detalle.datosPoliza && detalle.datosPoliza.fechaVigenciaDesde
              ? moment(detalle.datosPoliza.fechaVigenciaDesde).add(
                  detalle.datosPoliza.cantidadCuotas,
                  "months"
                )
              : moment(detalle.vigenciaDesde).add(
                  detalle.cantidadCuotas,
                  "months"
                ),
          valid: true,
          focus: false,
        },
        premioTotalSelected: {
          value: detalle.datosPoliza
            ? detalle.datosPoliza.premioTotal
            : detalle.datosEmision.precioInformado * detalle.cantidadCuotas,
          valid: true,
          focus: false,
        },
        codigoOrganizadorSelected: detalle.codigoOrganizador,
        codigoProductorSelected: detalle.codigoProductor,
        codigoVendedorSelected: detalle.codigoVendedor,
      };
    },
    [getDetalleSolicitudEmisionFailed.toString()]: (
      state: DetalleSolicitudEmisionState
    ): DetalleSolicitudEmisionState => {
      return {
        ...state,
        loadingError: true,
      };
    },
    [postEmisionManualInited.toString()]: (
      state: DetalleSolicitudEmisionState
    ): DetalleSolicitudEmisionState => {
      return {
        ...state,
        creatingPoliza: true,
        isLoading: true,
      };
    },
    [postEmisionManualCompleted.toString()]: (
      state: DetalleSolicitudEmisionState
    ): DetalleSolicitudEmisionState => {
      return {
        ...state,
        polizaCreada: true,
        creatingPoliza: false,
        isLoading: false,
      };
    },
    [postEmisionManualFailed.toString()]: (
      state: DetalleSolicitudEmisionState
    ): DetalleSolicitudEmisionState => {
      return {
        ...state,
        creatingPoliza: false,
        isLoading: false,
      };
    },
    [patchRechazarSolicitudInited.toString()]: (
      state: DetalleSolicitudEmisionState
    ): DetalleSolicitudEmisionState => {
      return {
        ...state,
      };
    },
    [patchRechazarSolicitudCompleted.toString()]: (
      state: DetalleSolicitudEmisionState
    ): DetalleSolicitudEmisionState => {
      return {
        ...state,
        solicitudRechazada: true,
      };
    },
    [patchRechazarSolicitudFailed.toString()]: (
      state: DetalleSolicitudEmisionState
    ): DetalleSolicitudEmisionState => {
      return {
        ...state,
      };
    },
    [getDatosPagoInited.toString()]: (
      state: DetalleSolicitudEmisionState
    ): DetalleSolicitudEmisionState => {
      return {
        ...state,
      };
    },
    [getDatosPagoCompleted.toString()]: (
      state: DetalleSolicitudEmisionState,
      action: Action<DatosPagoState>
    ): DetalleSolicitudEmisionState => {
      let datosPago = action.payload.datosPago;
      let fechaVencimientoTarjetaCredito =
        action.payload.fechaVencimientoTarjetaCredito;
      return {
        ...state,
        datosPago: { datosPago, fechaVencimientoTarjetaCredito },
      };
    },
    [getDatosPagoFailed.toString()]: (
      state: DetalleSolicitudEmisionState
    ): DetalleSolicitudEmisionState => {
      return {
        ...state,
      };
    },
    [getLogsUsuarioInited.toString()]: (
      state: DetalleSolicitudEmisionState
    ): DetalleSolicitudEmisionState => {
      return {
        ...state,
        logsUsuario: null,
        isLoadingLog: true,
      };
    },
    [getLogsUsuarioCompleted.toString()]: (
      state: DetalleSolicitudEmisionState,
      action: Action<{ logsUsuario: GetLogSolicitudResponse }>
    ): DetalleSolicitudEmisionState => {
      return {
        ...state,
        logsUsuario: action.payload.logsUsuario,
        isLoadingLog: false,
      };
    },
    [getLogsUsuarioFailed.toString()]: (
      state: DetalleSolicitudEmisionState
    ): DetalleSolicitudEmisionState => {
      return {
        ...state,
      };
    },
    [clearDatosPago.toString()]: (
      state: DetalleSolicitudEmisionState
    ): DetalleSolicitudEmisionState => {
      return {
        ...state,
        datosPago: null,
      };
    },
    [clearData.toString()]: (): DetalleSolicitudEmisionState => {
      return {
        ...detalleSolicitudEmisionInitialState,
      };
    },
    [setEmail.toString()]: (
      state: DetalleSolicitudEmisionState,
      action: Action<{ emailSelected: CampoFormulario<string | null> }>
    ): DetalleSolicitudEmisionState => {
      return {
        ...state,
        emailSelected: action.payload.emailSelected,
      };
    },
    [setDominio.toString()]: (
      state: DetalleSolicitudEmisionState,
      action: Action<{ dominioSelected: CampoFormulario<string | null> }>
    ): DetalleSolicitudEmisionState => {
      return {
        ...state,
        dominioSelected: action.payload.dominioSelected,
      };
    },
    [setVigenciaDesde.toString()]: (
      state: DetalleSolicitudEmisionState,
      action: Action<{
        vigenciaDesdeSelected: CampoFormulario<FechaFormulario>;
      }>
    ): DetalleSolicitudEmisionState => {
      const vigenciaDesde = action.payload.vigenciaDesdeSelected;
      const vigenciaHasta = new Date(vigenciaDesde.value as string | Date);
      const vigencia = state.vigenciaSelected;

      return {
        ...state,
        vigenciaDesdeSelected: vigenciaDesde,
        vigenciaHastaSelected: {
          value: new Date(
            vigenciaHasta.setMonth(vigenciaHasta.getMonth() + vigencia.value)
          ),
          valid: true,
          focus: false,
        },
      };
    },
    [setVigenciaHasta.toString()]: (
      state: DetalleSolicitudEmisionState,
      action: Action<{
        vigenciaHastaSelected: CampoFormulario<FechaFormulario>;
      }>
    ): DetalleSolicitudEmisionState => {
      return {
        ...state,
        vigenciaHastaSelected: action.payload.vigenciaHastaSelected,
      };
    },
    [setVigencia.toString()]: (
      state: DetalleSolicitudEmisionState,
      action: Action<{ vigenciaSelected: CampoFormulario<number> }>
    ): DetalleSolicitudEmisionState => {
      const vigencia = action.payload.vigenciaSelected;
      const vigenciaDesde = state.vigenciaDesdeSelected;
      return {
        ...state,
        vigenciaSelected: vigencia,
        vigenciaHastaSelected: {
          value: moment(vigenciaDesde.value).add(vigencia.value, "months"),
          valid: true,
          focus: false,
        },
      };
    },
    [setCantidadCuotas.toString()]: (
      state: DetalleSolicitudEmisionState,
      action: Action<{ cantidadCuotasSelected: CampoFormulario<number> }>
    ): DetalleSolicitudEmisionState => {
      return {
        ...state,
        cantidadCuotasSelected: action.payload.cantidadCuotasSelected,
      };
    },
    [setNroMotor.toString()]: (
      state: DetalleSolicitudEmisionState,
      action: Action<{ nroMotorSelected: CampoFormulario<string | null> }>
    ): DetalleSolicitudEmisionState => {
      return {
        ...state,
        nroMotorSelected: action.payload.nroMotorSelected,
      };
    },
    [setNroChasis.toString()]: (
      state: DetalleSolicitudEmisionState,
      action: Action<{ nroChasisSelected: CampoFormulario<string | null> }>
    ): DetalleSolicitudEmisionState => {
      return {
        ...state,
        nroChasisSelected: action.payload.nroChasisSelected,
      };
    },
    [setPremioTotal.toString()]: (
      state: DetalleSolicitudEmisionState,
      action: Action<{
        premioTotalSelected: CampoFormulario<string | number | null>;
      }>
    ): DetalleSolicitudEmisionState => {
      return {
        ...state,
        premioTotalSelected: action.payload.premioTotalSelected,
      };
    },
    [setSumaAsegurada.toString()]: (
      state: DetalleSolicitudEmisionState,
      action: Action<{
        sumaAseguradaSelected: CampoFormulario<string | number | null>;
      }>
    ): DetalleSolicitudEmisionState => {
      return {
        ...state,
        sumaAseguradaSelected: action.payload.sumaAseguradaSelected,
      };
    },
    [setNumeroPoliza.toString()]: (
      state: DetalleSolicitudEmisionState,
      action: Action<{ numeroPolizaSelected: CampoFormulario<string | null> }>
    ): DetalleSolicitudEmisionState => {
      return {
        ...state,
        numeroPolizaSelected: action.payload.numeroPolizaSelected,
      };
    },
    [setPolizaFileName.toString()]: (
      state: DetalleSolicitudEmisionState,
      action: Action<{ polizaFileNameSelected: CampoFormulario<string | null> }>
    ): DetalleSolicitudEmisionState => {
      return {
        ...state,
        polizaFileNameSelected: action.payload.polizaFileNameSelected,
      };
    },
    [setTarjetaFileName.toString()]: (
      state: DetalleSolicitudEmisionState,
      action: Action<{
        tarjetaFileNameSelected: CampoFormulario<string | null>;
      }>
    ): DetalleSolicitudEmisionState => {
      return {
        ...state,
        tarjetaFileNameSelected: action.payload.tarjetaFileNameSelected,
      };
    },
    [setCedulaMercosurFileName.toString()]: (
      state: DetalleSolicitudEmisionState,
      action: Action<{
        cedulaMercosurFileNameSelected: CampoFormulario<string | null>;
      }>
    ): DetalleSolicitudEmisionState => {
      return {
        ...state,
        cedulaMercosurFileNameSelected:
          action.payload.cedulaMercosurFileNameSelected,
      };
    },
    [setCuponeraFileName.toString()]: (
      state: DetalleSolicitudEmisionState,
      action: Action<{
        cuponeraFileNameSelected: CampoFormulario<string | null>;
      }>
    ): DetalleSolicitudEmisionState => {
      return {
        ...state,
        cuponeraFileNameSelected: action.payload.cuponeraFileNameSelected,
      };
    },
    [setIsProcessingPoliza.toString()]: (
      state: DetalleSolicitudEmisionState,
      action: Action<{ isProcessingPoliza: boolean }>
    ): DetalleSolicitudEmisionState => {
      return {
        ...state,
        isProcessingPoliza: action.payload.isProcessingPoliza,
      };
    },
    [setIsProcessingTarjeta.toString()]: (
      state: DetalleSolicitudEmisionState,
      action: Action<{ isProcessingTarjeta: boolean }>
    ): DetalleSolicitudEmisionState => {
      return {
        ...state,
        isProcessingTarjeta: action.payload.isProcessingTarjeta,
      };
    },
    [setIsProcessingCedulaMercosur.toString()]: (
      state: DetalleSolicitudEmisionState,
      action: Action<{ isProcessingCedulaMercosur: boolean }>
    ): DetalleSolicitudEmisionState => {
      return {
        ...state,
        isProcessingCedulaMercosur: action.payload.isProcessingCedulaMercosur,
      };
    },
    [setIsProcessingCuponera.toString()]: (
      state: DetalleSolicitudEmisionState,
      action: Action<{ isProcessingCuponera: boolean }>
    ): DetalleSolicitudEmisionState => {
      return {
        ...state,
        isProcessingCuponera: action.payload.isProcessingCuponera,
      };
    },
    [setFotoFrontalFileName.toString()]: (
      state: DetalleSolicitudEmisionState,
      action: Action<{
        fotoFrontalFileNameSelected: CampoFormulario<string | null>;
      }>
    ): DetalleSolicitudEmisionState => {
      return {
        ...state,
        fotoFrontalFileNameSelected: action.payload.fotoFrontalFileNameSelected,
      };
    },
    [setFotoTraseraFileName.toString()]: (
      state: DetalleSolicitudEmisionState,
      action: Action<{
        fotoTraseraFileNameSelected: CampoFormulario<string | null>;
      }>
    ): DetalleSolicitudEmisionState => {
      return {
        ...state,
        fotoTraseraFileNameSelected: action.payload.fotoTraseraFileNameSelected,
      };
    },
    [setFotoLateralIzquierdoFileName.toString()]: (
      state: DetalleSolicitudEmisionState,
      action: Action<{
        fotoLateralIzquierdoFileNameSelected: CampoFormulario<string | null>;
      }>
    ): DetalleSolicitudEmisionState => {
      return {
        ...state,
        fotoLateralIzquierdoFileNameSelected:
          action.payload.fotoLateralIzquierdoFileNameSelected,
      };
    },
    [setFotoLateralDerechoFileName.toString()]: (
      state: DetalleSolicitudEmisionState,
      action: Action<{
        fotoLateralDerechoFileNameSelected: CampoFormulario<string | null>;
      }>
    ): DetalleSolicitudEmisionState => {
      return {
        ...state,
        fotoLateralDerechoFileNameSelected:
          action.payload.fotoLateralDerechoFileNameSelected,
      };
    },
    [setCuota.toString()]: (
      state: DetalleSolicitudEmisionState,
      action: Action<{ cuotaSelected: CuotaFormulario }>
    ): DetalleSolicitudEmisionState => {
      let id = action.payload.cuotaSelected.id;
      state.cuotasSelected[id] = action.payload.cuotaSelected;
      return {
        ...state,
        cuotasSelected: state.cuotasSelected,
      };
    },
    [setBase64File.toString()]: (
      state: DetalleSolicitudEmisionState,
      action: Action<{ base64File: Base64File }>
    ): DetalleSolicitudEmisionState => {
      let id = action.payload.base64File.id;
      state.base64Files[id] = action.payload.base64File;
      let filesCount = Object.keys(state.base64Files).length;
      return {
        ...state,
        base64Files: state.base64Files,
        downloadFiles: filesCount === 4,
      };
    },
    [clearBase64File.toString()]: (
      state: DetalleSolicitudEmisionState
    ): DetalleSolicitudEmisionState => {
      return {
        ...state,
        base64Files: {},
        downloadFiles: false,
      };
    },
    [setSubmitting.toString()]: (
      state: DetalleSolicitudEmisionState,
      action: Action<{ submitting: boolean }>
    ): DetalleSolicitudEmisionState => {
      return {
        ...state,
        submitting: action.payload.submitting,
        emailSelected: { ...state.emailSelected, focus: false },
        dominioSelected: { ...state.dominioSelected, focus: false },
        nroMotorSelected: { ...state.nroMotorSelected, focus: false },
        nroChasisSelected: { ...state.nroChasisSelected, focus: false },
        premioTotalSelected: { ...state.premioTotalSelected, focus: false },
        numeroPolizaSelected: { ...state.numeroPolizaSelected, focus: false },
        vigenciaDesdeSelected: { ...state.vigenciaDesdeSelected, focus: false },
        vigenciaHastaSelected: { ...state.vigenciaHastaSelected, focus: false },
      };
    },
    [clearCuotas.toString()]: (
      state: DetalleSolicitudEmisionState
    ): DetalleSolicitudEmisionState => {
      return {
        ...state,
        cuotasSelected: {},
      };
    },
  },
  detalleSolicitudEmisionInitialState
);

export {
  detalleSolicitudEmisionReducer,
  getDetalleSolicitudEmision,
  postEmisionManual,
  patchRechazarSolicitud,
  getDatosPago,
  getLogsUsuario,
  clearDatosPago,
  setEmail,
  setDominio,
  setNroChasis,
  setNroMotor,
  setPremioTotal,
  setSumaAsegurada,
  setNumeroPoliza,
  setPolizaFileName,
  setTarjetaFileName,
  setCedulaMercosurFileName,
  setCuponeraFileName,
  setCuota,
  setVigenciaDesde,
  setVigenciaHasta,
  setVigencia,
  clearData,
  setSubmitting,
  clearCuotas,
  setBase64File,
  clearBase64File,
  setFotoFrontalFileName,
  setFotoTraseraFileName,
  setFotoLateralDerechoFileName,
  setFotoLateralIzquierdoFileName,
  setCantidadCuotas,
  setIsProcessingPoliza,
  setIsProcessingTarjeta,
  setIsProcessingCedulaMercosur,
  setIsProcessingCuponera,
};
